/**
 * 이 파일은 Aui 의 일부입니다. (https://aui.moimz.tools)
 *
 * Aui 컴포넌트 내부 엘리먼츠 클래스를 정의한다.
 *
 * @file /src/base/Element.ts
 * @author Arzz <arzz@arzz.com>
 * @license MIT License
 * @modified 2026. 5. 27.
 */
import * as parent from '@base/Base';
import { Component } from '@base/Component';
import { Dom } from '@utils/Dom';
import { Html } from '@utils/Html';
import { Exceptions } from '@utils/Exceptions';

export interface Listeners<T> extends parent.Listeners<T> {
    /**
     * 엘리먼트가 렌더링 되었을 때
     */
    render?: (me: T) => void;

    /**
     * 엘리먼트가 제거되었을 때
     */
    remove?: () => void;
}

export interface Properties<T> extends parent.Properties<T> {
    /**
     * 엘리먼트 너비(px)
     */
    width?: number;

    /**
     * 엘리먼트 너비(rem)
     */
    length?: number;

    /**
     * 엘리먼트 높이(px)
     */
    height?: number;

    /**
     * 엘리먼트 최대너비
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    maxWidth?: number | string;

    /**
     * 엘리먼트 최대높이
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    maxHeight?: number | string;

    /**
     * 엘리먼트 최소너비
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    minWidth?: number | string;

    /**
     * 엘리먼트 최소높이
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    minHeight?: number | string;

    /**
     * 엘리먼트 숨김여부
     */
    hidden?: boolean;

    /**
     * 이벤트리스너
     */
    listeners?: Listeners<T>;
}

export abstract class Element extends parent.Base {
    /**
     * 엘리먼트 타입
     * 하위 클래스에서 직접 타입을 지정하고, 해당 타입은 엘리먼트 DOM 에 추가된다.
     */
    abstract type: string;

    /**
     * 부모 컴포넌트
     */
    component: Component | null = null;

    /**
     * 엘리먼트 너비(px)
     */
    width: number | null;

    /**
     * 엘리먼트 너비(rem)
     */
    length: number | null;

    /**
     * 엘리먼트 높이(px)
     */
    height: number | null;

    /**
     * 엘리먼트 최대너비
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    maxWidth: number | string | null;

    /**
     * 엘리먼트 최대높이
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    maxHeight: number | string | null;

    /**
     * 엘리먼트 최소너비
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    minWidth: number | string | null;

    /**
     * 엘리먼트 최소높이
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     */
    minHeight: number | string | null;

    /**
     * 엘리먼트 숨김여부
     */
    hidden: boolean = false;

    /**
     * 엘리먼트 DOM
     */
    $element: Dom = null;

    /**
     * 렌더링여부
     */
    rendered: boolean = false;

    /**
     * Element 를 생성한다.
     */
    constructor(public properties: Properties<Element> = null) {
        super(properties);

        this.width = this.properties?.width ?? null;
        this.length = this.properties?.length ?? null;
        this.height = this.properties?.height ?? null;
        this.maxWidth = this.properties?.maxWidth ?? null;
        this.maxHeight = this.properties?.maxHeight ?? null;
        this.minWidth = this.properties?.minWidth ?? null;
        this.minHeight = this.properties?.minHeight ?? null;
        this.hidden = this.properties?.hidden ?? false;
    }

    /**
     * 부모 컴포넌트를 설정한다.
     *
     * @param component - 현재 엘리먼트를 가진 컴포넌트
     */
    setComponent(component: Component): void {
        this.component = component;
    }

    /**
     * 부모 컴포넌트를 가져온다.
     *
     * @returns 부모 컴포넌트
     */
    getComponent(): Component | null {
        return this.component ?? null;
    }

    /**
     * 엘리먼트의 DOM 을 가져온다.
     * 엘리먼트를 컴포넌트에 append 한 뒤 render() 함수를 호출하여 실제 엘리먼트의 DOM 을 처리한다.
     *
     * @returns 엘리먼트의 DOM 구조
     */
    $getElement(): Dom {
        if (this.$element == null) {
            const $dom = document.createElement('aui-element') as AuiElement;
            $dom.setElement(this);
            this.$element = Html.el($dom);
            this.$element.setAttr('data-type', this.type);

            if (this.width !== null) {
                this.$element.setStyle('width', this.width + 'px');
            } else if (this.length !== null) {
                this.$element.setStyle('width', this.length + 'rem');
            }

            if (this.height !== null) {
                this.$element.setStyle('height', this.height + 'px');
            }

            if (this.maxWidth !== null) {
                this.maxWidth = typeof this.maxWidth == 'number' ? this.maxWidth + 'px' : this.maxWidth;
                this.$element.setStyle('max-width', this.maxWidth);
            }

            if (this.maxHeight !== null) {
                this.maxHeight = typeof this.maxHeight == 'number' ? this.maxHeight + 'px' : this.maxHeight;
                this.$element.setStyle('max-height', this.maxHeight);
            }

            if (this.minWidth !== null) {
                this.minWidth = typeof this.minWidth == 'number' ? this.minWidth + 'px' : this.minWidth;
                this.$element.setStyle('min-width', this.minWidth);
            }

            if (this.minHeight !== null) {
                this.minHeight = typeof this.minHeight == 'number' ? this.minHeight + 'px' : this.minHeight;
                this.$element.setStyle('min-height', this.minHeight);
            }
        }

        return this.$element;
    }

    /**
     * 엘리먼트 너비(px)를 설정한다.
     *
     * @param width - 너비
     */
    setWidth(width: number): void {
        if (width === null) {
            this.width = null;
            this.$getElement().setStyle('width', null);
            if (this.length !== null) {
                this.setLength(this.length);
            }
            return;
        }

        this.width = width;
        this.$getElement().setStyle('width', this.width + 'px');
    }

    /**
     * 엘리먼트 너비(rem)를 설정한다.
     *
     * @param length - 너비
     */
    setLength(length: number): void {
        if (length === null) {
            this.length = null;
            this.$getElement().setStyle('width', null);
            if (this.width !== null) {
                this.setWidth(this.width);
            }
            return;
        }

        this.length = length;
        this.$getElement().setStyle('width', this.length + 'rem');
    }

    /**
     * 엘리먼트 높이(px)를 설정한다.
     *
     * @param height - 높이
     */
    setHeight(height: number): void {
        if (height === null) {
            this.height = null;
            this.$getElement().setStyle('height', null);
            return;
        }

        this.height = height;
        this.$getElement().setStyle('height', this.height + 'px');
    }

    /**
     * 엘리먼트 최대너비(px)를 설정한다.
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     *
     * @param maxWidth - 최대너비
     */
    setMaxWidth(maxWidth: string | number): void {
        if (maxWidth === null) {
            this.maxWidth = null;
            this.$getElement().setStyle('max-width', null);
            return;
        }

        this.maxWidth = typeof maxWidth == 'number' ? maxWidth + 'px' : maxWidth;
        this.$getElement().setStyle('max-width', this.maxWidth);
    }

    /**
     * 엘리먼트 최대높이(px)를 설정한다.
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     *
     * @param maxHeight - 최대높이
     */
    setMaxHeight(maxHeight: string | number): void {
        if (maxHeight === null) {
            this.maxHeight = null;
            this.$getElement().setStyle('max-height', null);
            return;
        }

        this.maxHeight = typeof maxHeight == 'number' ? maxHeight + 'px' : maxHeight;
        this.$getElement().setStyle('max-height', this.maxHeight);
    }

    /**
     * 엘리먼트 최소너비를 설정한다.
     * 숫자일 경우 px, 그 외 단위를 포함할 경우 단위를 포함한 문자
     *
     * @param minWidth - 최소너비
     */
    setMinWidth(minWidth: string | number): void {
        if (minWidth === null) {
            this.minWidth = null;
            this.$getElement().setStyle('min-width', null);
            return;
        }

        this.minWidth = typeof minWidth == 'number' ? minWidth + 'px' : minWidth;
        this.$getElement().setStyle('min-width', this.minWidth);
    }

    /**
     * 엘리먼트 최소높이를 설정한다.
     *
     * @param minHeight - 최소높이
     */
    setMinHeight(minHeight: string | number): void {
        if (minHeight === null) {
            this.minHeight = null;
            this.$getElement().setStyle('min-height', null);
            return;
        }

        this.minHeight = typeof minHeight == 'number' ? minHeight + 'px' : minHeight;
        this.$getElement().setStyle('min-height', this.minHeight);
    }

    /**
     * 엘리먼트 숨김여부를 설정한다.
     *
     * @param hidden - 숨김여부
     */
    async setHidden(hidden: boolean): Promise<void> {
        if (this.hidden == hidden) {
            return;
        }

        this.hidden = hidden;

        if (this.hidden == true) {
            this.$getElement().hide();
        } else {
            this.$getElement().show();
        }
    }

    /**
     * 엘리먼트를 보인다.
     */
    async show(): Promise<void> {
        await this.setHidden(false);
        this.$getElement().removeClass('hidden');
        if (this.isRendered() == false) {
            await this.render();
        }
    }

    /**
     * 엘리먼트를 숨긴다.
     */
    async hide(): Promise<void> {
        await this.setHidden(true);
        this.$getElement().addClass('hidden');
    }

    /**
     * 엘리먼트의 보임여부를 가져온다.
     *
     * @returns 보임여부
     */
    isShow(): boolean {
        return this.isRendered() == true && this.isHidden() == false;
    }

    /**
     * 엘리먼트 숨김여부를 가져온다.
     *
     * @returns 숨김여부
     */
    isHidden(): boolean {
        return this.hidden;
    }

    /**
     * 엘리먼트가 렌더링되었는지 확인한다.
     *
     * @returns 렌더링여부
     */
    isRendered(): boolean {
        return this.rendered;
    }

    /**
     * 엘리먼트 DOM 을 렌더링한다.
     * 하위 클래스에서 엘리먼트의 DOM 콘텐츠를 정의한다.
     */
    abstract render(): Promise<void>;

    /**
     * 엘리먼트를 제거한다.
     *
     * @param removeDom - 엘리먼트 DOM 을 제거할지 여부
     */
    async remove(removeDom: boolean = true): Promise<void> {
        if (removeDom == true && this.$element !== null && this.$element.isConnected() == true) {
            this.$element.remove();
        }

        this.$element = null;

        await this.onRemove();
        super.destroy();
    }

    /**
     * destroy() 를 호출할 수 없도록 처리
     */
    destroy(): void {
        Exceptions.warn('destroy() is disabled for Element. Use remove() instead.');
    }

    /**
     * 엘리먼트가 렌더링이 완료되었을 때 이벤트를 처리한다.
     */
    async onRender(): Promise<void> {
        this.rendered = true;
        await this.fireEvent('render', [this]);
    }

    /**
     * 엘리먼트가 제거되었을 때 이벤트를 처리한다.
     */
    async onRemove(): Promise<void> {
        await this.fireEvent('remove', [this]);
    }

    /**
     * 엘리먼트 DOM 이 연결되었을 때 이벤트를 처리한다.
     */
    async onConnect(): Promise<void> {}

    /**
     * 엘리먼트 DOM 이 연결해제되었을 때 이벤트를 처리한다.
     */
    async onDisconnect(): Promise<void> {}
}

class AuiElement extends HTMLElement {
    element: Element = null;
    setElement(element: Element) {
        this.element = element;
    }

    connectedCallback() {
        this.element?.onConnect();
    }

    disconnectedCallback() {
        this.element?.onDisconnect();
    }
}
customElements.define('aui-element', AuiElement);
